import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  IActiveLearningIssue,
  IActiveLearningAnswers,
} from "../../../../../state";
import { Context } from "../../../../../studentStore";
import { IAnswer } from "../../../../../constants/active_learning";
import hintImage13 from "../../../../../assets/images/ActiveLearning/hint/img_13.webp";
import styles from "../../ActiveLearningIssue.module.scss";
import CommentList from "../../../../../molecules/ActiveLearning/CommentList";
import FeedbackCommentInput from "../../FeedbackCommentInput";

interface IActiveLearningIssue2Props {
  issue?: IActiveLearningIssue;
  answers: IActiveLearningAnswers[];
  setAnswers: Dispatch<SetStateAction<IActiveLearningAnswers[]>>;
  saveAnswers: () => void;
}

export interface ICurrentAnswer extends IAnswer {
  from_student_id: number;
  to_student_id: number;
  value: string;
}

export const Issue2 = (props: IActiveLearningIssue2Props) => {
  const { state } = useContext(Context);
  const currentIssueNo = 2;
  const question1 = useMemo(() => {
    return props.issue?.questions.find(q => q.order_num === 1);
  }, [props.issue?.questions]);

  const [answerToQ1, setAnswerToQ1] = useState<ICurrentAnswer>({
    id: null,
    issue_no: currentIssueNo,
    question_no: question1?.order_num || NaN,
    question_id: question1?.id || NaN,
    from_student_id: state.student?.id || NaN,
    to_student_id: NaN,
    value: "",
  });

  const syncStoreAnswerToState = useCallback(
    (
      questionNo: number,
      setter: Dispatch<React.SetStateAction<ICurrentAnswer>>,
      toStudentId: number,
    ) => {
      const targetAnswer = state.active_learning_answers.find(
        answer =>
          answer.issue_no === currentIssueNo &&
          answer.question_no === questionNo &&
          answer.contents?.to_student_id === toStudentId,
      );
      const answer: ICurrentAnswer = {
        id: targetAnswer?.id || null,
        issue_no: currentIssueNo,
        question_no: questionNo,
        question_id: targetAnswer?.question_id || question1?.id || 0,
        from_student_id: state.student?.id || NaN,
        to_student_id: toStudentId,
        value: targetAnswer?.contents?.value || "",
      };
      setter({ ...answer });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.active_learning_answers],
  );

  useEffect(() => {
    // Store側のデータを各ステートに同期
    syncStoreAnswerToState(
      question1?.order_num || NaN,
      setAnswerToQ1,
      answerToQ1.to_student_id,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.active_learning_answers, syncStoreAnswerToState, question1]);

  useEffect(() => {
    const excludedAnswer = props.answers.filter(
      answer => answer.issue_no !== currentIssueNo,
    );
    const currentAnswers: IActiveLearningAnswers[] = [
      {
        id: answerToQ1.id,
        issue_no: answerToQ1.issue_no,
        question_no: answerToQ1.question_no,
        question_id: answerToQ1.question_id,
        contents: {
          from_student_id: answerToQ1.from_student_id,
          to_student_id: answerToQ1.to_student_id,
          value: answerToQ1.value,
        },
      },
    ];
    props.setAnswers([...excludedAnswer, ...currentAnswers]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerToQ1, props.setAnswers]);

  const answersI2Q1 = state.active_learning_answers.filter(
    answer => answer.issue_no === currentIssueNo && answer.question_no === 1,
  );

  const feedbackAnswers = answersI2Q1.map(answer => {
    const students = state.classes.flatMap(classItem => classItem.students);
    const matchedStudent = students.find(
      student => student.id === answer.contents.to_student_id,
    );

    return {
      id: answer.id,
      student_id: matchedStudent?.id,
      full_name: `${matchedStudent?.last_name} ${matchedStudent?.first_name}`,
      contents: answer.contents,
    };
  });

  return (
    <div className={styles.issue}>
      <div className={styles.header}>
        <span className={styles.no}>
          WORK
          {props.issue?.no && props.issue?.no < 10
            ? `0${props.issue?.no}`
            : props.issue?.no}
        </span>
        <p className={styles.title}>{props.issue?.title}</p>
        <p className={styles.read}>
          これまで考えてきた「問い」/「課題」/解決のアイデアを相手に伝わりやすくまとめて発表しよう。
          <br />
          発表を聞き終わったら、発表した人に良かった点を伝えたり、アドバイスしたりしよう。
        </p>
      </div>
      <img src={hintImage13} alt="発表を聞くときに良かった点を探すヒント" />
      {question1 && (
        <>
          <div className={styles.themEntry}>
            <p className={styles.title}>{question1?.title}</p>
            <p className={styles.read}>
              発表を聞いて良かった点やアドバイスを具体的に入力して、発表者に送信しよう。
            </p>
          </div>
          <FeedbackCommentInput
            question_order_num={question1.order_num}
            issue_no={currentIssueNo}
            classes={state.classes}
            current_student_id={state.student?.id}
            answer={answerToQ1}
            setAnswer={setAnswerToQ1}
            feedbackAnswers={feedbackAnswers}
            active_learning_answers={state.active_learning_answers}
            saveAnswers={props.saveAnswers}
          />
          <div className={styles.commentList}>
            <div className={styles.label}>
              <p className={styles.title}>送ったコメント</p>
            </div>
            <CommentList commentList={feedbackAnswers} />
          </div>
        </>
      )}
    </div>
  );
};
export default Issue2;
